import { getFleetCategoriesQueryKey, getFleetTypesQueryKey, listSitesQueryKey } from './generated/@tanstack/react-query.gen';
import { queryOptions } from '@tanstack/react-query';
import { cache } from 'react';
import { env } from '@/env';
import type { CountryCode } from '@/types/CountryCode';
import { type GetFleetCategoriesData, type Site, getFleetCategories as getFleetCategoriesDefault, getFleetTypes as getFleetTypesDefault, listHirePeriods, listSites } from './generated';
import { client } from './generated/client.gen';
import { type Options } from './generated/sdk.gen';

export { client } from './generated/client.gen';

export type * from './generated/types.gen';

export const rentalsApiClient = client;
rentalsApiClient.setConfig({
    baseUrl: env.NEXT_PUBLIC_RENTALS_API_BASE_URL,
});

export const getSites = cache(async () => {
    const response = await listSites({
        next: {
            revalidate: 180,
            tags: ['sites'],
        },
    });

    return response.data || [];
});

export const getRegions = cache(async (activeCountryCode?: CountryCode) => {
    const sites = await getSites();
    return mapSitesToRegion({ sites, countryCode: activeCountryCode });
});

export interface Region {
    countryCode: CountryCode;
    country: string;
    sites: Site[];
}

export function findSiteInRegion(regions: Region[], siteCode: string) {
    for (const region of regions) {
        const site = region.sites.find((s) => s.siteCode === siteCode);
        if (site) {
            return site;
        }
    }
}

const regions: Region[] = [
    {
        country: 'Australia',
        countryCode: 'au',
        sites: [],
    },
    {
        country: 'New Zealand',
        countryCode: 'nz',
        sites: [],
    },
];
export function mapSitesToRegion({ sites, countryCode }: { sites: Site[]; countryCode?: string }) {
    const sitesByCountry = Object.groupBy(sites, (s) => s.countryCode);

    const mapped = regions.map((region) => ({
        ...region,
        sites: (sitesByCountry[region.countryCode] ?? []).toSorted((a, b) => a.name.localeCompare(b.name)),
    }));

    if (countryCode) {
        const idx = mapped.findIndex((r) => r.countryCode === countryCode);
        if (idx > 0) {
            mapped.unshift(mapped.splice(idx, 1)[0]);
        }
    }

    return mapped;
}

const fleetTypeSortOrder = ['campervan', 'motorhome', 'car'];
export const getFleetTypes = cache(async () => {
    const response = await getFleetTypesDefault({
        next: {
            revalidate: 180,
            tags: ['fleet-types'],
        },
    });

    return (response.data || []).sort((a, b) => {
        const indexA = fleetTypeSortOrder.indexOf(a.slug);
        const indexB = fleetTypeSortOrder.indexOf(b.slug);

        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        }

        if (indexA !== -1) {
            return -1;
        }
        if (indexB !== -1) {
            return 1;
        }
        return 0;
    });
});

export const getHirePeriods = cache(async () => {
    const date = await listHirePeriods({
        next: {
            revalidate: 180,
            tags: ['hire-periods'],
        },
    });

    return date.data || [];
});

export const getFleetCategories = cache(async () => {
    const date = await getFleetCategoriesDefault({
        next: {
            revalidate: 180,
            tags: ['fleet-categories'],
        },
    });

    return date.data || [];
});

export const getFleetTypesOptions = (options?: Parameters<typeof getFleetTypesQueryKey>[0]) =>
    queryOptions({
        queryFn: getFleetTypes,
        queryKey: getFleetTypesQueryKey(options),
    });

export const getFleetCategoriesOptions = (options?: Options<GetFleetCategoriesData>) =>
    queryOptions({
        queryFn: getFleetCategories,
        queryKey: getFleetCategoriesQueryKey(options),
    });

export const listSitesOptions = (options?: Parameters<typeof listSitesQueryKey>[0]) =>
    queryOptions({
        queryFn: getSites,
        queryKey: listSitesQueryKey(options),
    });
